import {
    Button,
    Dialog,
    DialogDescription,
    DialogHeader,
    Grid,
    GridRow,
    Icon,
    Input,
    InputProps,
    useIntl,
} from "@metsooutotec/modes-react-components"
import { FC, useEffect, useState } from "react"
import customerStore, { CustomerStoreResults } from "features/organizations/customers/store/customer-store"
import { handleUploadFileClick, NavigationModalOptions, resetRecordFormData } from "components/utils/modal-helper"
import { validateFile, validateText } from "validators/inputValidators"
import { CreateAssetSection } from "../../models/asset-section-models"
import { ModalTypes } from "features/organizations/customers/utils/customer-modal-helper"

/*Important note: the fields and erros recored follow the ids of the props such as assetSectionName*/

/**
 * Props for the Create Asset Section component.
 */
interface AssetModalProps {
    isOpen: boolean | undefined
    onClose: (passed: boolean, assetSectionId?: string, navigationAssetSectionOpetions?: NavigationModalOptions) => void
    assetId: string | undefined
    siteId: string | undefined
    customerId: string | undefined
}

interface AssetSectionFormField {
    id: string
    label: string
    placeholder: string
    type: string
    validate?: (value: string) => string
    onFileUpload?: () => void
    unSelectFile?: () => void
}

const AssetSectionModal: FC<AssetModalProps> = ({ isOpen, onClose, assetId, siteId, customerId }) => {
    const { formatMessage } = useIntl()
    const [isLoading, setLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<{ isError: boolean; errorMessage: string }>({ isError: false, errorMessage: "" })
    const [selectedSvg, setSelectedSvg] = useState<File | null>(null)

    const [formData, setFormData] = useState<Record<string, string>>({
        assetSectionName: "",
        assetSectionDescription: "",
        uploadSvgName: selectedSvg?.name as string,
    })
    const [formErrors, setFormErrors] = useState<Record<string, string>>({
        assetSectionName: "",
        assetSectionDescription: "",
        uploadSvgName: "",
    })

    const validateAssetSectionForm = (formData: Record<string, string>): Record<string, string> => {
        const errors: Record<string, string> = {}
        errors.assetSectionName = validateText(formData.assetSectionName)
        errors.assetSectionDescription = validateText(formData.assetSectionDescription)
        errors.uploadSvgName = validateFile(formData.uploadSvgName)
        return errors
    }
    useEffect(() => {
        resetRecordFormData(setFormData)
    }, [])

    const resetForm = (): void => {
        resetFormData()
        setFormErrors({
            assetSectionName: "",
            assetSectionDescription: "",
            uploadSvgName: "",
        })
        setSelectedSvg(null)
    }

    const resetFormData = () => {
        setFormData({
            assetSectionName: "",
            assetSectionDescription: "",
            uploadSvgName: "",
        })
    }
    const handleChange = (fieldName: string, value: string) => {
        isError.isError = false
        setFormData(prevData => ({
            ...prevData,
            [fieldName]: value,
        }))
    }
    const handleValidation = (fieldName: string, validationError: string | any): void => {
        setFormErrors(prevErrors => ({
            ...prevErrors,
            [fieldName]: validationError,
        }))
    }
    const onCloseProcess = (): void => {
        resetForm()
        setIsError({ isError: false, errorMessage: "" })
        onClose(false)
    }
    const onClearClick = (inputId: string) => {
        setFormData(prevData => ({
            ...prevData,
            [inputId]: "",
        }))
        setFormErrors(prevErrors => ({
            ...prevErrors,
            [inputId]: "",
        }))
    }

    const handleSvgClick = () => {
        handleUploadFileClick(".svg", setSelectedSvg, setFormData, setFormErrors, "uploadSvgName")
    }

    const clearSvg = () => {
        console.log("unselect:", selectedSvg?.name)
        setFormData(prevData => ({
            ...prevData,
            uploadSvgName: "",
        }))
        setSelectedSvg(null)
    }
    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault()
        try {
            setLoading(true)

            // Validate the form
            const errors: Record<string, string> = validateAssetSectionForm(formData)
            const hasErrors = Object.values(errors).some(error => error !== "")

            if (hasErrors) {
                setIsError({ isError: true, errorMessage: "There are validation errors, update the form texts" })
                setFormErrors(errors)
                setLoading(false) // Reset loading state
                return
            }

            const { assetSectionName, assetSectionDescription } = formData
            const assetSectionData: CreateAssetSection = {
                customerId: customerId || "",
                siteId: siteId || "",
                assetId: assetId || "",
                name: assetSectionName,
                description: assetSectionDescription,
                assetSectionSvg: selectedSvg || new File([], "empty_file"),
            }

            const requestResult: CustomerStoreResults = await customerStore.createEntity(
                assetSectionData as CreateAssetSection,
                ModalTypes.AssetSectionModal,
            )

            if (!requestResult.passed) {
                setIsError({ isError: true, errorMessage: requestResult.message })
                setLoading(false)
                return
            }

            if (!requestResult.passed) {
                setIsError({ isError: true, errorMessage: requestResult.message })
                setLoading(false)
                return
            }

            //Handle success
            console.log("Form submitted successfully!", requestResult.message, requestResult.entityId)
            resetForm() // Reset form data and errors
            const routeParam: NavigationModalOptions = { type: "created" }
            onClose(true, requestResult.entityId, routeParam)
        } catch (error) {
            setIsError({ isError: true, errorMessage: "Form submission error, please read logs" })
            console.error("Form submission error:", error)
        } finally {
            setLoading(false)
        }
    }
    const labels = {
        assetSectionName: formatMessage("assetSectionModal.name"),
        assetSectionDescription: formatMessage("assetSectionModal.description"),
        confirm: formatMessage("modal.confirm"),
        cancel: formatMessage("modal.cancel"),
        header: formatMessage("assetSectionModal.header"),
        description: formatMessage("modal.description"),
        uploadSvg: formatMessage("assetSectionModal.uploadSvg"),
    }
    const inputConfig: AssetSectionFormField[] = [
        {
            id: "assetSectionName",
            label: labels.assetSectionName,
            placeholder: `Type ${labels.assetSectionName} here...`,
            type: "string",
            validate: validateText,
        },
        {
            id: "assetSectionDescription",
            label: labels.assetSectionDescription,
            placeholder: `Type ${labels.assetSectionDescription} here...`,
            type: "string",
            validate: validateText,
        },
        {
            id: "uploadSvgName",
            label: labels.uploadSvg,
            placeholder: `select from files...`,
            type: "string",
            onFileUpload: handleSvgClick,
            unSelectFile: clearSvg,
            validate: validateFile,
        },
    ]
    const renderInputs = (): React.ReactNode => {
        return (
            <>
                {/* First set of fields */}
                {inputConfig.slice(0, 2).map((input: AssetSectionFormField, index) => {
                    const { id, label, validate, type, placeholder } = input
                    const clearIcon = formData[id] && <Icon className='icon-cross-circle-fill-16' onClick={() => onClearClick(id)} />
                    const inputProps: InputProps = {
                        id,
                        label,
                        placeholder,
                        validate,
                        onValidation: (validationError: string | undefined) => handleValidation(id, validationError),
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(id, e.target.value),
                        type,
                        error: formErrors[id],
                        spanning: true,
                        heightClass: "medium",
                        required: true,
                        uiRequired: true,
                        value: formData[id],
                        icon: clearIcon,
                    }

                    return (
                        <GridRow key={id} style={{ marginBottom: "var(--size-m)" }}>
                            <Input {...inputProps} />
                        </GridRow>
                    )
                })}
                {/* Second set of fields */}
                {inputConfig.slice(2).map((input: AssetSectionFormField, index) => {
                    const { id, label, type, placeholder, onFileUpload, unSelectFile, validate } = input
                    const icons = (
                        <>
                            {formData[id] && <Icon className='icon-cross-circle-fill-16' onClick={unSelectFile} style={{ marginRight: "2px" }} />}
                            <Icon className='icon-upload-16' onClick={onFileUpload} />
                        </>
                    )
                    const inputProps: InputProps = {
                        id,
                        label,
                        placeholder,
                        validate,
                        onValidation: (validationError: string | undefined) => handleValidation(id, validationError),
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(id, e.target.value),
                        type,
                        spanning: true,
                        heightClass: "medium",
                        error: formErrors[id],
                        readOnly: true,
                        onClick: onFileUpload,
                        required: true,
                        uiRequired: true,
                        value: formData[id],
                        icon: icons,
                    }
                    return (
                        <GridRow key={id} style={{ marginBottom: "var(--size-m)" }}>
                            <Input {...inputProps} />
                        </GridRow>
                    )
                })}
            </>
        )
    }
    const renderButtons = (): React.ReactNode => {
        const buttonsConfig = [
            { label: labels.confirm, onClick: handleSubmit, primary: true },
            { label: labels.cancel, onClick: onCloseProcess, primary: false },
        ]
        return buttonsConfig.map((button, index) => {
            const { label, onClick, primary } = button
            const buttonGridStyle =
                index === 0
                    ? {
                          marginBottom: "var(--size-m)",
                      }
                    : {}
            return (
                <GridRow key={label} style={buttonGridStyle}>
                    <Button
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={onClick}
                        flexible={true}
                        children={label}
                        centered
                        size={"medium"}
                        primary={primary}
                    />
                </GridRow>
            )
        })
    }
    return (
        <Grid>
            <Dialog dimmer open={isOpen} size={"small"} style={{ maxWidth: "512px" }}>
                <DialogHeader username={""}>{labels.header}</DialogHeader>
                {isError.isError && <p style={{ color: "red" }}>{isError.errorMessage}</p>}
                <DialogDescription style={{ marginBottom: "var(--size-xxl)" }}>{labels.description}</DialogDescription>
                {renderInputs()}
                <div style={{ marginBottom: "var(--size-xxl)" }} />
                {renderButtons()}
            </Dialog>
        </Grid>
    )
}

export default AssetSectionModal
